/* eslint-disable import/no-unresolved */
/* eslint-disable no-debugger */
/* eslint-disable no-useless-escape */
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack5';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { LoadingButton } from '@material-ui/lab';
import {
  Box,
  Card,
  Grid,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Autocomplete,
  Checkbox,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import useAuth from 'src/hooks/useAuth';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import CaseConvertor from '../../common-components/CaseConvertor';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { createUsers, updateUsers, resetCreateUsersStatus, resetUsersError } from '../../../redux/slices/users__';
import { getProjectListByClient } from '../../../redux/slices/client';
import { getClientListByProject } from '../../../redux/slices/project';

// ----------------------------------------------------------------------

AddUserForm.propTypes = {
  editId: PropTypes.number,
  setVisibleComponent: PropTypes.func,
  currentUser: PropTypes.object,
  CLIENT_OPTION: PropTypes.array,
  PROJECT_OPTION: PropTypes.array,
  USER_ROLE_LIST: PropTypes.array
};

export default function AddUserForm({
  editId,
  currentUser = {},
  setVisibleComponent,
  CLIENT_OPTION,
  PROJECT_OPTION,
  USER_ROLE_LIST
}) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { updateUserDetails } = useAuth();
  const { error, createUsersStatus, isLoading } = useSelector((state) => state.users__);
  const [showPassword, setShowPassword] = useState(false);
  const { clientListByProject } = useSelector((state) => state.project);
  const { projectListByClient } = useSelector((state) => state.client);

  useEffect(() => {
    setFieldValue('client', clientListByProject?.length > 0 ? clientListByProject.map((item) => item) : []);
  }, [clientListByProject]);

  useEffect(() => {
    if (error || createUsersStatus) {
      enqueueSnackbar(
        (createUsersStatus && (!editId ? 'New User Created Successfully' : 'User Updated Successfully')) ||
          (error && error),
        { variant: error ? 'error' : 'success' }
      );
      dispatch(resetUsersError());
    }
    if (createUsersStatus) {
      setVisibleComponent('List');
      formik.resetForm();
      dispatch(resetCreateUsersStatus());
    }
  }, [error, createUsersStatus]);

  const NewUserSchema = Yup.object().shape({
    fname: Yup.string().required('First name is required'),
    lname: Yup.string().required('Last name is required'),
    email: Yup.string().required('Email is required').email(),
    password: editId
      ? ''
      : Yup.string()
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
            'Password must contain atleast 8 characters that includes one Uppercase letter, one Lowercase letter, one Numeric character and one Special character'
          )
          .required('Password is required'),
    phoneNumber: Yup.string().required('Phone number is required'),
    role: Yup.mixed().required('Role is required'),
    client: Yup.array()
      .of(
        Yup.object().shape({
          value: Yup.mixed(),
          label: Yup.mixed()
        })
      )
      .min(1, 'client is required'),
    project: Yup.array()
      .of(
        Yup.object().shape({
          value: Yup.mixed(),
          label: Yup.mixed()
        })
      )
      .min(1, 'project is required')
  });

  const editTempData = {
    editFirstName: currentUser.first_name ? currentUser.first_name : '',
    editLastName: currentUser.last_name ? currentUser.last_name : '',
    editEmail: currentUser.email ? currentUser.email : '',
    editPhoneNumber: currentUser.phone ? currentUser.phone : '',
    editRole: currentUser.user_role_id ? USER_ROLE_LIST.filter((a) => a.id === currentUser.user_role_id) : '',
    editClient: currentUser.clients ? currentUser.clients : [],
    editProject: currentUser.projects ? currentUser.projects : []
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fname: editId ? editTempData.editFirstName : '',
      lname: editId ? editTempData.editLastName : '',
      email: editId ? editTempData.editEmail : '',
      password: '',
      phoneNumber: editId ? editTempData.editPhoneNumber : '',
      role: editId ? editTempData.editRole[0] : '',
      client: editId ? editTempData.editClient : [],
      project: editId ? editTempData.editProject : [],
      loginDetails: true
    },
    validationSchema: NewUserSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const userData = {
          first_name: CaseConvertor(values.fname),
          last_name: CaseConvertor(values.lname),
          email: values.email,
          password: values.password,
          user_role: values.role.id,
          clients: values.client.map((a) => a.id),
          projects: values.project.map((a) => a.id),
          phone: values.phoneNumber,
          reset_password_link: values.loginDetails
        };
        await dispatch(!editId ? createUsers(userData) : updateUsers(userData, editId));
        setSubmitting(false);
        await updateUserDetails();
      } catch (error) {
        console.error(error);
        setErrors(error);
      }
    }
  });
  const { errors, values, touched, handleSubmit, getFieldProps, setFieldValue } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <FormikProvider value={formik}>
      <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Card sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    {...getFieldProps('fname')}
                    error={Boolean(touched.fname && errors.fname)}
                    helperText={touched.fname && errors.fname}
                  />
                  <TextField
                    fullWidth
                    required
                    label="Last Name"
                    {...getFieldProps('lname')}
                    error={Boolean(touched.lname && errors.lname)}
                    helperText={touched.lname && errors.lname}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="Email Address"
                    {...getFieldProps('email')}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    autoComplete="new-password"
                    required={!editId}
                    type={showPassword ? 'text' : 'password'}
                    {...getFieldProps('password')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: '15px' }}>
                          <IconButton onClick={handleShowPassword} edge="end">
                            <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                          </IconButton>
                        </InputAdornment>
                      ),
                      style: {
                        paddingRight: '0px',
                        position: 'relative'
                      }
                    }}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="Phone Number"
                    {...getFieldProps('phoneNumber')}
                    error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                  />
                  <Autocomplete
                    fullWidth
                    disablePortal
                    name="role"
                    value={values?.role ? values?.role : null}
                    options={USER_ROLE_LIST}
                    onChange={(e, value) => setFieldValue('role', value)}
                    getOptionLabel={(option) => (option ? option.access_profile_name : '')}
                    renderInput={(params) => (
                      <TextField
                        error={Boolean(touched.role && errors.role)}
                        helperText={touched.role && errors.role}
                        required
                        {...params}
                        label="Role"
                        margin="none"
                      />
                    )}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <Autocomplete
                    multiple
                    fullWidth
                    name="client"
                    value={values?.client ? values?.client : null}
                    // options={CLIENT_OPTION}
                    options={
                      clientListByProject.length > 0
                        ? clientListByProject.map((item) => item)
                        : CLIENT_OPTION.map((item) => item)
                    }
                    onChange={(e, value) => {
                      // console.log('value', value);
                      setFieldValue('client', value);
                      const arrayid = value.map((item) => item.id);
                      dispatch(getProjectListByClient(arrayid));
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} />
                        {option.client_name}
                      </li>
                    )}
                    getOptionLabel={(option) => (option ? option?.client_name : '')}
                    renderInput={(params) => (
                      <TextField
                        error={Boolean(touched.client && errors.client)}
                        helperText={touched.client && errors.client}
                        required
                        {...params}
                        label="Client"
                        margin="none"
                      />
                    )}
                  />
                  <Autocomplete
                    fullWidth
                    multiple
                    name="project"
                    value={values?.project ? values?.project : null}
                    onChange={(e, value) => {
                      console.log('value', value);
                      setFieldValue('project', value);
                      if (value.length > 0) {
                        const arrayid = value.map((item) => item.id);
                        dispatch(getClientListByProject(arrayid));
                      } else {
                        setFieldValue('client', []);
                      }
                    }}
                    options={projectListByClient || PROJECT_OPTION}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} />
                        {option.project_name}
                      </li>
                    )}
                    getOptionLabel={(option) => (option ? option?.project_name : '')}
                    renderInput={(params) => (
                      <TextField
                        error={Boolean(touched.project && errors.project)}
                        helperText={touched.project && errors.project}
                        required={!editId}
                        {...params}
                        label="Project"
                        margin="none"
                      />
                    )}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <FormControlLabel
                    control={<Checkbox {...getFieldProps('loginDetails')} checked={values.loginDetails} />}
                    label={<Typography variant="button">Send Reset Link Password</Typography>}
                  />
                </Stack>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <LoadingButton type="submit" variant="contained" loading={isLoading}>
                    {!editId ? 'Create User' : 'Save Changes'}
                  </LoadingButton>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  );
}
